import {Drawer, PreviewFormValues} from 'components/customer/Preview3D/types';
import {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';
import {makeZeroIfUndefined} from 'components/customer/Preview3D/helpers';
import {RoomPlannerFormValues} from 'components/customer/RoomPlanner/types';
import {getProductAttributes} from 'components/customer/RoomPlanner/helpers/getProductAttributes';
import {PRODUCT_ID} from 'shared/constants/Products';

type FormValues = {
    height: number;
    depth: number;
    drawerGap: number;
    drawers: Drawer[];
    drawerTop: number;
    drawerBottom: number;
    drawerLeft: number;
    drawerRight: number;
    ovenOpeningHeight: number;
    microwaveOpeningHeight: number;
    width: number;
    hasDoor: boolean;
    lowerShelfHeight: number;
    drawerFaceHeights: number[];
    toeKickHeight: number;
    drawerAmount: number;
};

export type DrawerValues = {
    faceHeight: number;
    depth: number;
    position: number;
    bottomPosition: number;
    bottomYPosition: number;
    drawerBottomYPosition: number;
    bottomDepth: number;
    depthOffset: number;
    index: number;
    drawerBackHeight: number;
    isInsetDrawer: boolean;
};

const NONE_DRAWER_TYPE = 82;

export const getDrawerFormValues = (values: PreviewFormValues) => {
    return {
        height: makeZeroIfUndefined(values.cabinet_height),
        width: makeZeroIfUndefined(values.cabinet_width),
        depth: makeZeroIfUndefined(values.cabinet_depth),
        drawerGap: makeZeroIfUndefined(values.cabinet_drawer_gap),
        drawers: values.drawers as unknown as Drawer[],
        drawerTop: makeZeroIfUndefined(values.cabinet_drawer_top),
        drawerBottom: makeZeroIfUndefined(values.cabinet_drawer_bottom),
        drawerLeft: makeZeroIfUndefined(values.cabinet_drawer_left),
        drawerRight: makeZeroIfUndefined(values.cabinet_drawer_right),
        ovenOpeningHeight: makeZeroIfUndefined(values.oven_opening_height),
        microwaveOpeningHeight: makeZeroIfUndefined(
            values.microwave_opening_height
        ),
        lowerShelfHeight: makeZeroIfUndefined(values.cabinet_partition_height),
        hasDoor: typeof values.cabinet_door_gap !== 'undefined',
        drawerFaceHeights: values.drawer_face_height
            ? values.drawer_face_height
            : null,
        toeKickHeight: makeZeroIfUndefined(values.cabinet_toekick),
        drawerAmount: makeZeroIfUndefined(values.drawer_amount),
    };
};

export const generateDrawerPosition = (
    values: RoomPlannerFormValues,
    variables: TemplateVariable,
    formValues: FormValues,
    hasDoor: boolean,
    scale = 1
) => {
    return generateDrawers(
        formValues.drawers,
        formValues,
        variables,
        values,
        false,
        variables?.isWallOvenProduct
            ? Number(values.cabinet_total_drawer_height)
            : formValues.height,
        formValues.width,
        hasDoor,
        scale
    );
};

export const sumProperty = (array: Drawer[], property: keyof Drawer) => {
    return array.reduce(
        /* eslint-disable-next-line security/detect-object-injection --
         * Object sink is not a risk here.
         **/
        (acc, obj) => acc + parseFloat(obj[property]?.toString()),
        0
    );
};

export const generateDrawers = (
    drawers: Drawer[],
    formValues: FormValues,
    variables: TemplateVariable,
    values: RoomPlannerFormValues,
    isInnerDrawer = false,
    containerHeight = formValues.height,
    containerWidth = formValues.width,
    hasDoor = false,
    scale = 1
) => {
    const drawerValues: DrawerValues[] = [];
    const drawerLength = drawers?.length;
    const carcaseThickness = values.cabinet_carc_thickness;
    const exteriorThickness = values.cabinet_ext_thickness * scale;
    const {isPantry, hasInsetDrawer, hasMicrowaveDrawer} = getProductAttributes(
        variables,
        values
    );
    const isDrawerComponent =
        Number(values.cabinet_type) === PRODUCT_ID.DRAWER_COMPONENT;
    const isCustomInnerDrawers =
        isInnerDrawer &&
        !!formValues?.drawerAmount &&
        !!formValues?.drawers?.length &&
        !isDrawerComponent;

    if (variables?.hasToeKick) {
        const totalFaceHeight =
            drawers?.reduce(
                (sum, drawer) => sum + (drawer?.drawer_face_height ?? 0),
                0
            ) ?? 0;

        if (totalFaceHeight > containerHeight - formValues.toeKickHeight) {
            drawers = drawers?.map((drawer) => {
                if (!drawer?.drawer_runner_specs || !drawer?.drawer_face_height)
                    return drawer;
                const runnerMatch = drawer.drawer_runner_specs.match(/^(\d+)x/);
                if (!runnerMatch) return drawer;
                const runnerHeight = parseFloat(runnerMatch[1]);
                if (drawer.drawer_face_height === runnerHeight) return drawer;
                return {...drawer, drawer_face_height: runnerHeight};
            });
        }
    }

    for (let index = 0; index < drawerLength; index++) {
        let faceHeight =
            drawers && drawers[Number(index)]
                ? drawers[Number(index)].drawer_face_height
                : 0;

        if (
            Boolean(formValues.microwaveOpeningHeight) &&
            !variables?.isWallOvenProduct
        ) {
            faceHeight =
                containerHeight -
                formValues.microwaveOpeningHeight -
                formValues.drawerTop -
                formValues.drawerBottom -
                1;
        }

        const slicedDrawer = drawers.slice(index + 1);
        const gapCount = slicedDrawer?.length;

        const totalHeight = sumProperty(slicedDrawer, 'drawer_face_height');

        let position = 0;
        let unshift = false;
        const toeKickHeight = Boolean(variables.hasToeKick)
            ? formValues.toeKickHeight + carcaseThickness
            : 0;
        const isInsetDrawer = hasInsetDrawer;

        if (isCustomInnerDrawers) {
            // custom inner drawers
            const positionToBottom =
                formValues?.drawers[Number(index)]?.position_to_bottom;
            position = positionToBottom;
            if (drawerLength === index + 1) {
                position = positionToBottom + carcaseThickness;
            }
        } else if (isInnerDrawer) {
            let topDrawerHeightTotal = faceHeight * drawerLength;

            if (isPantry) {
                topDrawerHeightTotal = faceHeight * (drawerLength - 1) + 50;
            }

            const totalGapSpace = containerHeight - topDrawerHeightTotal;
            const gap = totalGapSpace / (drawerLength + 1);

            if (drawerLength === index + 1)
                position = formValues.drawerBottom + exteriorThickness;
            else {
                unshift = true;

                position =
                    gap * (index + 1) +
                    faceHeight * index +
                    faceHeight +
                    gap / 2;
            }
        } else {
            if (gapCount > 0) {
                position =
                    totalHeight +
                    parseFloat(formValues.drawerGap?.toString()) * gapCount +
                    parseFloat(formValues.drawerBottom?.toString()) +
                    toeKickHeight -
                    (isInsetDrawer ? carcaseThickness : 0);
            } else {
                position =
                    formValues.drawerBottom +
                    toeKickHeight -
                    (isInsetDrawer ? carcaseThickness : 0);
            }

            if (hasDoor) {
                position = containerHeight - faceHeight - formValues.drawerTop;
            }
        }

        const drawer = drawers[Number(index)];
        const drawerRunnerSpecs = drawer?.drawer_runner_specs;
        const drawerRunnerDepth = drawer?.drawer_runner_depth;
        const drawerRunnerHeight = drawer?.drawer_runner_height;
        const match =
            drawerRunnerSpecs && parseInt(drawerRunnerSpecs) != -1
                ? drawerRunnerSpecs?.match(/(\d+)x(\d+)/)
                : [
                      null,
                      String(Number(drawer?.drawer_face_height) / 2),
                      String(formValues.depth - 50),
                  ];
        let depth =
            (drawerRunnerDepth ?? match)
                ? parseFloat(match[2])
                : formValues.depth;

        let depthOffset =
            isInsetDrawer || hasMicrowaveDrawer
                ? values.cabinet_ext_thickness
                : 0;

        if (variables.innerDrawerOnly) {
            depth -= carcaseThickness;
            containerWidth -= exteriorThickness;
        }

        let bottomPosition =
            drawerLength == index + 1 &&
            !formValues.hasDoor &&
            !isInnerDrawer &&
            !isInsetDrawer &&
            !hasMicrowaveDrawer
                ? (formValues.drawerBottom > 0 ? formValues.drawerBottom : 0) +
                  toeKickHeight
                : position;

        if (variables?.isWallOvenProduct && drawerLength == index + 1) {
            bottomPosition = position = formValues.drawerBottom;
        }

        let drawerBackHeight =
            drawerRunnerHeight ??
            (match ? parseFloat(match[1]) - exteriorThickness : 150);

        if (variables?.isBinProduct) {
            const binDrawerRunnerHeight = 150;
            drawerBackHeight = binDrawerRunnerHeight;
            bottomPosition = containerHeight - binDrawerRunnerHeight * 2;
        } else if (variables?.isBinWithInnerDrawerProduct) {
            const binDrawerRunnerHeight = 150;
            drawerBackHeight = binDrawerRunnerHeight;
            bottomPosition = containerHeight - binDrawerRunnerHeight * 1.3;
        } else if (variables.innerDrawerOnly) {
            drawerBackHeight = match ? parseFloat(match[1]) : 0;
            bottomPosition = 0;
            position = 0;
        }

        const onlyShowDrawerFace = drawers[Number(index)].drawer_type
            ? drawers[Number(index)].drawer_type === NONE_DRAWER_TYPE
            : false;

        if (isInnerDrawer) depthOffset += 10;

        const drawerItem = {
            faceHeight,
            depth,
            position,
            bottomPosition:
                bottomPosition + (isInnerDrawer ? 0 : carcaseThickness),
            index,
            bottomYPosition: depth + depthOffset,
            drawerBottomYPosition: depth - carcaseThickness + depthOffset,
            bottomDepth: depth - carcaseThickness,
            depthOffset,
            drawerBackHeight,
            onlyShowDrawerFace,
            isInsetDrawer,
        };

        if (unshift) drawerValues.unshift(drawerItem);
        else drawerValues.push(drawerItem);
    }

    const drawerWidthOffset = variables?.drawerWidthOffset ?? 100;
    const runnerOffset = variables?.runnerOffset ?? 54;

    const drawerWidth = containerWidth - drawerWidthOffset;
    const sideOffset = runnerOffset + carcaseThickness;
    const backHorizontalPosition =
        containerWidth - drawerWidthOffset + sideOffset;
    const drawerFaceWidth =
        containerWidth +
        carcaseThickness -
        formValues.drawerLeft -
        formValues.drawerRight -
        (isInnerDrawer ? carcaseThickness * 2 : 0);
    const drawerHorizontalPosition =
        formValues.drawerLeft + (isInnerDrawer ? carcaseThickness : 0);

    return {
        drawers: drawerValues,
        drawerFaceWidth,
        drawerHorizontalPosition,
        ...(hasInsetDrawer
            ? {
                  drawerInsetFaceWidth: drawerFaceWidth - carcaseThickness * 2,
                  drawerInsetHorizontalPosition:
                      drawerHorizontalPosition + carcaseThickness,
              }
            : {}),
        drawerWidth,
        backHorizontalPosition,
        runnerLeftHorizontalPosition: sideOffset,
        drawerWidthWithOffset: drawerWidth - carcaseThickness,
        backHorizontalPositionWithOffset:
            backHorizontalPosition - carcaseThickness,
        ovenPosition: containerHeight - formValues.ovenOpeningHeight,
        microwavePosition: containerHeight - formValues.microwaveOpeningHeight,
        drawerLeftGap: formValues.drawerLeft,
        drawerRightGap: formValues.drawerRight,
        isCustomInnerDrawers,
    };
};

export const cloneObjectNTimes = <T>(obj: T, n: number): T[] =>
    Array.from({length: n}, () => ({...obj}));

export const generateDrawerFace = (formValues: FormValues) => {
    const drawerFaces = [...(formValues.drawers ?? [])].reverse();
    let currentPosition = 0;

    const result = {
        drawers: drawerFaces.map((drawer, index) => {
            // Capture position before incrementing
            const position = currentPosition;

            // Advance by this drawer's height + gap for the next iteration
            currentPosition +=
                Number(drawer.drawer_face_height) +
                Number(formValues.drawerGap);

            return {
                index,
                faceHeight: drawer.drawer_face_height,
                position,
            };
        }),
    };

    return result;
};

export const generateInnerDrawer = (
    values: RoomPlannerFormValues,
    variables: TemplateVariable,
    formValues: FormValues,
    innerDrawerFaceHeight = 0
) => {
    const innerDrawerCount = variables.innerDrawerCount || values.drawer_amount;
    if (!innerDrawerCount) return;
    const isPantry = variables?.shelfType === 'PANTRY';

    const faceHeight =
        innerDrawerFaceHeight !== 0 ? innerDrawerFaceHeight : 200;
    const shallowFaceHeight =
        innerDrawerFaceHeight !== 0 ? innerDrawerFaceHeight : 120;

    const innerDrawer = {
        drawer_face_height: faceHeight,
        drawer_runner_specs: `${faceHeight}x${
            formValues.depth - 100
        } : White D`,
        drawer_type: 1,
    };

    let innerDrawers = values.drawers;

    // validate drawers
    const invalidDrawers = values.drawers?.some(
        (drawer) => !Number(drawer.drawer_face_height)
    );

    if ((!values.drawer_amount && !values.drawers?.length) || invalidDrawers) {
        innerDrawers = cloneObjectNTimes(innerDrawer, innerDrawerCount);
        if (isPantry) {
            innerDrawers = innerDrawers.map((drawer, index) => {
                if (index === innerDrawers?.length - 2) {
                    drawer.drawer_runner_specs = `${shallowFaceHeight}x${
                        formValues.depth - 100
                    } : White D`;
                }
                return drawer;
            });
        }
    }

    // Backend sends drawer_amount = 1 incorrectly in 3DRP (works fine in the
    // Product Visualiser). Clear it so generateDrawers uses the drawers array
    // length instead.
    if (innerDrawerCount > Number(formValues.drawerAmount))
        formValues.drawerAmount = undefined;

    const result = generateDrawers(
        innerDrawers,
        formValues,
        variables,
        values,
        true,
        variables.hasLowerShelf
            ? formValues.lowerShelfHeight
            : formValues.height,
        formValues.width,
        true
    );

    return result;
};

export const getRunnerDimensions = (runnerSpecs: string) => {
    const match = runnerSpecs.match(/(\d+)x(\d+)/);
    const height = match ? parseFloat(match[1]) : 0;
    const depth = match ? parseFloat(match[2]) : 0;
    return {height, depth};
};
